import { LevelPreviewUtil } from '../../Utils/LevelPreviewUtil';
import { EditorBacklog } from './EditorBacklog';
import { EditorStorageManager } from './EditorStorageManager';
import { DEFAULT_LEVEL_DATA } from '../LevelEditor/LevelEditor';

/* eslint-disable no-restricted-globals */
export class MapEditorSidePanel extends Phaser.Group {
  /**
   * constructor
   * @param {*} x
   * @param {*} y
   */
  constructor(x, y) {
    super(game);

    this.x = G.l(x);
    this.y = G.l(y);

    this.signals = {
      onPathChanged: new Phaser.Signal(),
    };

    this._state = game.state.getCurrentState();
    this._storageManager = EditorStorageManager.getInstance();

    this._levelNr = this._makeTextField(0, 0, 'LEVEL: --');
    this.add(this._levelNr);

    this._starsReq = this._makeTextField(0, 50, '--');
    this.add(this._starsReq);

    this._makeLevelPreviewImage();

    // create panel buttons
    this._makeLevelSwapButtons();
    this._makeChangeLevelNumButton();
    this._makeBacklogButtons();
    this._makePlaylevelButton();
    this._makeEditLevelButton();
    this._makenewLevelButton();
    this._makeCopyLevelButton();
    this._makeExportButton();
    this._makeResetAllButton();
    this._makeSaveStatusDisplay();
  }

  /**
   * make the level preview image
   */
  _makeLevelPreviewImage() {
    this._previewBitmap = game.add.bitmapData(400, 400);
    this._previewBitmapImg = this.add(this._previewBitmap.addToWorld(0, 100));
  }

  /**
   * make the level swap up/down buttons
   */
  _makeLevelSwapButtons() {
    // swap up button
    this._swapUpBtn = this._makeTextFieldButton(0, 500, 'SWAP UP', 30, () => {
      if (this._state.selectedLevels[0] !== undefined) {
        this._swapLevels(G.Helpers.levelDataMgr.getIndexOfLevelId(this._state.selectedLevels[0]) + 1);
      }
    });
    this.add(this._swapUpBtn);
    // swap down button
    this._swapDownBtn = this._makeTextFieldButton(200, 500, 'SWAP DOWN', 30, () => {
      if (this._state.selectedLevels[0] !== undefined) {
        this._swapLevels(Math.max(0, G.Helpers.levelDataMgr.getIndexOfLevelId(this._state.selectedLevels[0]) - 1));
      }
    });
    this.add(this._swapDownBtn);
  }

  /**
   * make button to change a levels #
   */
  _makeChangeLevelNumButton() {
    this._changeNumberBtn = this._makeTextFieldButton(450, 500, 'CHANGE NUMBER', 30, () => {
      G.Utils.debugPrompt('Change level number').then(((answer) => {
        const changeTo = parseInt(answer) - 1;
        if (isNaN(changeTo)) return;
        this._swapLevels(changeTo);
      }));
    });
    this.add(this._changeNumberBtn);
  }

  /**
   * make backlog buttons
   */
  _makeBacklogButtons() {
    // make add from backlog button
    this._backlog = this._makeTextFieldButton(0, 625, 'ADD FROM BACKLOG', 30, () => {
      const backlogViewer = new EditorBacklog();
      backlogViewer.onLevelSelected.add((id) => {
        G.Helpers.levelDataMgr.addLevelsToPath([id]);
        backlogViewer.close();
        this._state.map.editorRefreshButtons();
        this.signals.onPathChanged.dispatch();
      });
    });
    this.add(this._backlog);

    // make move to backlog button
    this._removeLevelsBtn = this._makeTextFieldButton(0, 675, 'MOVE TO BACKLOG', 30, () => { this._removeLevels(); });
    this.add(this._removeLevelsBtn);
  }

  /**
   * make the play / test level button
   */
  _makePlaylevelButton() {
    this._playLevelBtn = this._makeTextFieldButton(0, 750, 'PLAY', 30, () => {
      if (this._state.selectedLevels[0] !== undefined) {
        // eslint-disable-next-line prefer-destructuring
        G.lvlNr = this._state.selectedLevels[0];
        const levelData = {
          lvlIndex: G.Helpers.levelDataMgr.getIndexOfLevelId(this._state.selectedLevels[0]),
          debugMode: true,
        };
        game.state.start('Game', true, false, levelData);
      }
    });
    this.add(this._playLevelBtn);
  }

  /**
   * make the edit level button
   */
  _makeEditLevelButton() {
    this._editLevelBtn = this._makeTextFieldButton(0, 800, 'EDIT', 30, () => {
      if (this._state.selectedLevels[0] !== undefined) {
        game.state.start('Editor', true, false, this._state.selectedLevels[0]);
      }
    });
    this.add(this._editLevelBtn);
  }

  /**
   * make the new level button
   */
  _makenewLevelButton() {
    this._copyLevelBtn = this._makeTextFieldButton(0, 850, 'NEW LEVEL', 30, () => {
      this._createNewLevel();
    });
    this.add(this._copyLevelBtn);
  }

  /**
   * make the copy level button
   */
  _makeCopyLevelButton() {
    this._copyLevelBtn = this._makeTextFieldButton(0, 900, 'COPY LEVEL(s)', 30, () => {
      this._copyLevels();
    });
    this.add(this._copyLevelBtn);
  }

  /**
   * make the export button
   */
  _makeExportButton() {
    this._exportBtn = this._makeTextFieldButton(0, 1000, 'EXPORT ALL CHANGES', 30, () => {
      G.Helpers.levelDataMgr.exportLevels(this._storageManager.originalData);
    });
    this.add(this._exportBtn);
  }

  /**
   * make the reset all change button
   */
  _makeResetAllButton() {
    this._resetBtn = this._makeTextFieldButton(0, 1050, 'RESET ALL CHANGES', 30, async () => {
      const resetConfirmed = await G.Utils.confirmPrompt('Are you sure you would like to reset all unexported changes?');
      if (resetConfirmed) { // reset all changes to level data / path
        this._storageManager.resetAllChanges();
        const resetData = this._storageManager.originalData;
        G.json.levels = resetData;
        G.Helpers.levelDataMgr.init(resetData);
        game.state.start('EditorWorld');
      }
    });
    this.add(this._resetBtn);
  }

  /**
   * make the save status display
   */
  _makeSaveStatusDisplay() {
    this._saveSatus = this._makeTextField(0, 1100, '');
    this.updateSavedStatus();
  }

  /**
   * update editor saved status
   */
  updateSavedStatus() {
    const hasChanges = this._storageManager.hasModifications;
    if (hasChanges) {
      const success = this._storageManager.lastSaveSuccessfull;
      this._saveSatus.text = success ? 'AUTO-SAVED ✔️' : 'AUTO-SAVED ❌';
      this._saveSatus.alpha = 1;
    } else {
      this._saveSatus.text = 'NO CHANGES';
      this._saveSatus.alpha = 0.5;
    }
  }

  /**
   * make a text field instance
   * @param {number} x
   * @param {number} y
   * @param {string} text
   * @param {number} size
   * @returns {G.Text}
   */
  _makeTextField(x, y, text, size) {
    const textField = new G.Text(x, y, text, { style: 'editor', fontSize: size || 30 });
    this.add(textField);
    return textField;
  }

  /**
   * make a text field instance
   * @param {number} x
   * @param {number} y
   * @param {string} text
   * @param {number} size
   * @param {Function} callback
   * @returns {G.Text}
   */
  _makeTextFieldButton(x, y, text, size, callback) {
    const textField = this._makeTextField(x, y, text, size);
    textField.inputEnabled = true;
    textField.input.useHandCursor = true;
    textField.events.onInputDown.add(callback);
    return textField;
  }

  /**
   * swap level positions
   * @param {number} pos
   */
  _swapLevels(pos) {
    if (this._state.selectedLevels.length === 0) return;
    G.Helpers.levelDataMgr.changeLevelsOrder(this._state.selectedLevels, pos);
    this._state.selectLevel(this._state.selectedLevels);
    this.signals.onPathChanged.dispatch();
  }

  /**
   * create a new level from the default data
   */
  _createNewLevel() {
    const newLevelId = G.Helpers.levelDataMgr.createNewLevel(DEFAULT_LEVEL_DATA);
    const newLevelData = G.Helpers.levelDataMgr.getRawLevelById(newLevelId);
    this._storageManager.saveLevelChanges(newLevelId, newLevelData);
    this._state.fillSaveState3Stars();
    this._state.map.editorRefreshButtons();
    this._state.selectLevel(newLevelId);
    this.signals.onPathChanged.dispatch();
  }

  /**
   * copy the selected levels
   */
  _copyLevels() {
    const levelsToCopy = this._state.selectedLevels;
    levelsToCopy.forEach((levelToCopyId) => {
      const newLevelId = G.Helpers.levelDataMgr.copyLevel(levelToCopyId);
      const newLevelData = G.Helpers.levelDataMgr.getRawLevelById(newLevelId);
      this._storageManager.saveLevelChanges(newLevelId, newLevelData);
    });
    this._state.fillSaveState3Stars();
    this._state.map.editorRefreshButtons();
    this._state.selectLevel(levelsToCopy);
    this.signals.onPathChanged.dispatch();
  }

  /**
   * remove the selected levels
   * @param {Array} levels
   */
  _removeLevels() {
    if (this._state.selectedLevels.length === 0) return;
    G.Helpers.levelDataMgr.removeLevelsFromPath(this._state.selectedLevels);
    this._state.selectLevel(null);
    this.signals.onPathChanged.dispatch();
  }

  /**
   * refresh panel to the selected level status
   */
  refresh() {
    const state = game.state.getCurrentState();
    if (state.selectedLevels[0] === undefined) {
      this._levelNr.setText('LEVEL: --');
      this._starsReq.setText('--');
      this._previewBitmapImg.alpha = 0;
    } else {
      const lvlData = G.Helpers.levelDataMgr.getLevelById(state.selectedLevels[0]);
      this._previewBitmapImg.alpha = 1;
      LevelPreviewUtil.makePreview(lvlData, this._previewBitmap);
      this._levelNr.setText(`LEVEL: ${lvlData.levelNumber}  (level_${lvlData.id})`);
      this._starsReq.setText(lvlData.starsReq.toString());
    }
    this.updateSavedStatus();
  }
}
